import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import { TourSummaryOverlay, BigStopOverlay } from '@app/features/map/tour-summary-overlay/TourSummaryOverlay';
import { AppMapContainer } from '@app/new-app/NewMapContainer';
import { useSetRecoilState, useRecoilValue, useRecoilCallback, selector } from 'recoil';
import { atom_isDesktop, atom_tourState } from '@app/shared/state/tour.atom';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { atom_tourNotifications } from '@app/shared/state/notification.atom';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { useRef } from 'react';

// Mounts map once loading is done — skips all the init atom churn
const selector_mapReady = selector({
	key: 'mapPage_mapReady',
	get: ({ get }) => get(atom_tourStage) !== 'loading',
});
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InitialDataFetch } from '@app/features/initial-data-fetch/InitialDataFetch';
import { NotificationToast } from '@app/shared/components/NotificationToast';
import NavigationHelperContent from '@app/shared/components/NavigationHelperContent';
import { StateRestorer } from '@app/shared/components/StateRestorer';
import { GuideStepMiniPanel } from '@app/shared/components/GuideStepMiniPanel';
import { useRenderTracker } from '@app/shared/debug';
import {
	SmartAgentOverlay,
	MobileHeaderOverlay,
	MobileListOverlay,
	DesktopSidebarPanel,
	DesktopFloatingFilters,
	ModalOverlay,
} from './MapOverlays';

// Fires mapEntry notification once when tourStage becomes 'map'
const MapEntryNotifier = React.memo(function MapEntryNotifier() {
	const tourStage = useRecoilValue(atom_tourStage);
	const { fireTrigger } = useNotificationTrigger();
	const hasTriggeredRef = useRef(false);
	const getNotifications = useRecoilCallback(({ snapshot }) => () =>
		snapshot.getLoadable(atom_tourNotifications).getValue()
	, []);

	React.useEffect(() => {
		if (tourStage === 'map' && !hasTriggeredRef.current) {
			const notifs = getNotifications();
			if (Array.isArray(notifs) && notifs.length > 0) {
				hasTriggeredRef.current = true;
				fireTrigger('mapEntry');
			}
		}
	}, [tourStage, fireTrigger, getNotifications]);

	return null;
});

// Thin gate: only mounts BigStopOverlay after tourStage is 'map'
const BigStopGate = React.memo(function BigStopGate() {
	const tourStage = useRecoilValue(atom_tourStage);
	if (tourStage !== 'map') return null;
	return <BigStopOverlay />;
});

export function MapPage() {
	useRenderTracker('MapPage');
	const setIsDesktop = useSetRecoilState(atom_isDesktop);
	const isDesktop = useRecoilValue(atom_isDesktop);
	const { t } = useTranslation();

	// Block mobile back button navigation from tour to MyTours
	const blocker = useBlocker(({ nextLocation, historyAction }) => {
		return historyAction === 'POP' && nextLocation.pathname.startsWith('/mytours');
	});

	useEffect(() => {
		if (blocker.state === 'blocked') {
			if (window.confirm(t('on_tour.go_back_tour') as string)) {
				blocker.proceed();
			} else {
				blocker.reset();
			}
		}
	}, [blocker.state]);

	useEffect(() => {
		const handleResizeMode = () => {
			const width = typeof window !== 'undefined' ? window.innerWidth : 0;
			setIsDesktop(width >= 800);
		};

		handleResizeMode();
		window.addEventListener('resize', handleResizeMode);
		return () => window.removeEventListener('resize', handleResizeMode);
	}, [setIsDesktop]);

	const mapReady = useRecoilValue(selector_mapReady);

	return (
		<Box className="h-full w-full overflow-hidden" sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'white' }}>
			{/* Map + markers + clusters — only mounts once tour is loaded and stage is 'map' */}
			{mapReady && <AppMapContainer />}

			{/* Global services */}
			<InitialDataFetch />
			<StateRestorer />
			<NotificationToast />
			<MapEntryNotifier />

			{/* Overlays — each is React.memo with its own atom subscriptions */}
			<SmartAgentOverlay />
			{!isDesktop && <MobileHeaderOverlay />}
			{!isDesktop && <MobileListOverlay />}
			{isDesktop && mapReady && <DesktopSidebarPanel />}
			{isDesktop && mapReady && <DesktopFloatingFilters />}
			<ModalOverlay />
			<NavigationHelperContent />

			{/* Tour flow overlays */}
			<TourSummaryOverlay />
			<BigStopGate />

			{/* Guide-route turn-by-turn mini panel (gates itself on nav mode + guide-route) */}
			<GuideStepMiniPanel />
		</Box>
	);
}
